import { apiRequest } from '../helpers'

// Shared shape of both per-tenant app-registration status responses.
interface MicrosoftAppStatus {
  configured: boolean
  clientId?: string
  tenantId?: string
  // Canonical OAuth callback URL from the backend config.
  redirectUri?: string
  updatedAt?: string
}

interface AppRegistrationPayload {
  clientId: string
  tenantId?: string
  clientSecret: string
}

// The SSO and SharePoint app registrations expose identical status/save/delete
// calls, so build both from one helper.
function appRegistrationApi(path: string) {
  const base = `${process.env.REACT_APP_API_URL}${path}`

  return {
    status: (): Promise<MicrosoftAppStatus> => apiRequest(`${base}/status`, 'GET'),
    save: (payload: AppRegistrationPayload): Promise<{ ok?: boolean; message?: unknown }> =>
      apiRequest(base, 'POST', payload),
    remove: (): Promise<{ ok?: boolean }> => apiRequest(base, 'DELETE'),
  }
}

const ssoApp = appRegistrationApi('/microsoft/sso/connection')
const sharePointApp = appRegistrationApi('/microsoft/sharepoint-app/connection')

export const getMicrosoftSsoStatus = ssoApp.status
export const saveMicrosoftSsoConnection = ssoApp.save
export const deleteMicrosoftSsoConnection = ssoApp.remove

export const getMicrosoftSharePointAppStatus = sharePointApp.status
export const saveMicrosoftSharePointAppConnection = sharePointApp.save
export const deleteMicrosoftSharePointAppConnection = sharePointApp.remove

/**
 * Public (pre-login) check used by the login page to decide whether to offer
 * Microsoft sign-in for this tenant. Fails closed: any error hides the button.
 */
export async function isMicrosoftLoginEnabled(): Promise<boolean> {
  try {
    const result = await apiRequest(
      `${process.env.REACT_APP_API_URL}/microsoft/login/enabled`,
      'GET'
    )
    return !!result.enabled
  } catch {
    return false
  }
}

/** Public: whether the Microsoft integration is enabled fleet-wide. Fails open. */
export async function isMicrosoftEnabled(): Promise<boolean> {
  try {
    const result = await apiRequest(`${process.env.REACT_APP_API_URL}/microsoft/enabled`, 'GET')
    return result.enabled !== false
  } catch {
    return true
  }
}

export async function startMicrosoftConnect(): Promise<{ authorizationUrl?: string }> {
  const url = new URL(`${process.env.REACT_APP_API_URL}/microsoft/connect`)
  url.searchParams.set('origin', window.location.origin)

  return apiRequest(url.toString(), 'POST')
}

export async function disconnectMicrosoft(): Promise<{ ok?: boolean }> {
  return apiRequest(`${process.env.REACT_APP_API_URL}/microsoft/connection`, 'DELETE')
}
